import { GetAllResolvedEdiblesResponse } from "api-types";

import React from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";

import { NotInterestedRounded } from "@mui/icons-material";

import { useTranslation } from "../../../i18n";
import { AllergenChipOutlined } from "../../Chip/Chip";
import { DynamicDrawer } from "../../Drawer/DynamicDrawer";
import CloseIcon from "../../Icons/Close";
import { MappedProduct } from "./product.types";

interface ProductDrawerProps {
  product?: MappedProduct | null;
  linkedItems?: GetAllResolvedEdiblesResponse;
  open: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

/**
 * Extracts a unique list of ingredient names that contain a specific allergen
 * from both the main product and any directly linked nested products.
 */
const getAllergenIngredientNames = (
  product: MappedProduct | null | undefined,
  linkedItems: GetAllResolvedEdiblesResponse | undefined,
  allergenId: string
): string[] => {
  if (!product || !linkedItems) return [];

  const mainProduct = linkedItems.products[product.id];
  if (!mainProduct) return [];

  const mainIngredients = mainProduct.directlyLinkedIngredients || [];
  const nestedIngredients =
    mainProduct.directlyLinkedProducts?.flatMap((nestedProduct) => {
      const nested = linkedItems.products[nestedProduct.id];
      return nested?.directlyLinkedIngredients || [];
    }) || [];

  const allIngredients = [...mainIngredients, ...nestedIngredients];

  const matchedNames = allIngredients
    .filter((i) => i.allergens?.some((a) => a.igfdTaxAllergenId === allergenId))
    .map((i) => i.name);

  return Array.from(new Set(matchedNames));
};

/**
 * Displays a drawer with allergen information for a specific product,
 * including which ingredients (direct or nested) contain relevant allergens.
 */
export const ProductDrawer = ({
  product,
  open,
  linkedItems,
  onClose,
  containerRef,
}: ProductDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { oscar } = theme.palette;

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType="small"
      open={open}
      onClose={onClose}
      Header={
        <Stack alignItems="center" justifyItems="flex-start" gap={1}>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <NotInterestedRounded
              fontSize="small"
              sx={{ color: oscar.light.icon.danger, fontSize: 20 }}
            />
          </Stack>
          <Typography
            color={oscar.light.icon.danger}
            variant="headingRLight"
            textAlign="center"
          >
            {product?.name}
          </Typography>
        </Stack>
      }
      Footer={
        <CloseIcon
          color={oscar.light.icon.black}
          style={{ cursor: "pointer" }}
          height={32}
          width={32}
          onClick={onClose}
        />
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap={4}
        marginTop={4}
        width="100%"
      >
        <Stack
          sx={{
            backgroundColor: oscar.light.surface.neutral,
            padding: 2,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="caption"
            color={oscar.light.content.content1}
            align="center"
          >
            {t("product.canBeRemovedNotice")}
          </Typography>
        </Stack>

        {product?.allergens?.map((allergen) => (
          <Box
            key={allergen.igfdTaxAllergenId}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
          >
            <AllergenChipOutlined allergenName={allergen.allergenName} />
            <Stack alignItems="center" direction="row" gap={0.5}>
              <Typography
                color={oscar.light.content.content1}
                variant="labelXSLight"
                textAlign="left"
              >
                <Box component="span" sx={{ fontStyle: "italic" }}>
                  {t("product.contains")}
                </Box>{" "}
                {getAllergenIngredientNames(
                  product,
                  linkedItems,
                  allergen.igfdTaxAllergenId
                ).join(", ")}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Box>
    </DynamicDrawer>
  );
};
